/**
 * Product Information Management Page
 * 
 * Allows manufacturers to edit and update product information for existing batches,
 * including images, nutrition data, and custom content with confirmation workflows.
 */

import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { enhancedProductService } from '../services/enhanced-product.service';
import { productService } from '../services/product.service';
import { Button, Alert, Modal, Input } from '../components/ui';
import { formatBatchName } from '../lib/utils';
import ImageUpload, { UploadedImage } from '../components/ImageUpload';
import NutritionInformationForm from '../components/NutritionInformationForm';
import CustomContentForm from '../components/CustomContentForm';
import ManufacturingDetailsForm from '../components/ManufacturingDetailsForm';
import { ProductInfoAuditTrail } from '../components/ProductInfoAuditTrail';
// import { ProductInfoVersionComparison } from '../components/ProductInfoVersionComparison';
import {
  EnhancedProductDisplay,
  NutritionInformation,
  CustomProductContent,
  ManufacturingDetails,
  ProductInfoChangeType,
  ProductInfoAudit,
  ENHANCED_PRODUCT_INFO_CONSTANTS
} from '../../../backend/src/types/enhanced-product-info.types';

interface BatchInfo {
  id: string;
  productName: string;
  batchTitle?: string;
  serialStart: number;
  serialEnd: number;
  manufacturerName: string;
  createdAt: string;
}

interface UpdateConfirmation {
  isOpen: boolean;
  updateType: ProductInfoChangeType;
  data: any;
  changes: string[];
}

interface ExtendedUploadedImage extends UploadedImage {
  existingImage?: boolean;
}

export default function ProductInfoManagementPage() {
  const navigate = useNavigate();
  const { batchId } = useParams<{ batchId: string }>();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  // Note: user not currently used on this page
  const { user: _user } = useAuthStore();

  // State for batch and product information
  const [batch, setBatch] = useState<BatchInfo | null>(null);
  const [productInfo, setProductInfo] = useState<EnhancedProductDisplay | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [sectionErrors, setSectionErrors] = useState<Partial<Record<ProductInfoChangeType, string>>>({});

  // State for editing sections
  const [editingSection, setEditingSection] = useState<ProductInfoChangeType | null>(null);
  const [productType, setProductType] = useState<'manufactured' | 'produce'>('manufactured');
  const [produceDetails, setProduceDetails] = useState<{ harvestDate?: string; origin?: string; variety?: string; storageInstructions?: string; expiryDate?: string; bestBeforeDays?: number }>({});
  const [nutritionInfo, setNutritionInfo] = useState<Partial<NutritionInformation>>({});
  const [customContent, setCustomContent] = useState<Partial<CustomProductContent>>({});
  const [manufacturingDetails, setManufacturingDetails] = useState<Partial<ManufacturingDetails>>({});
  const [productImages, setProductImages] = useState<ExtendedUploadedImage[]>([]);
  const [imagesToDelete, setImagesToDelete] = useState<string[]>([]);

  // State for confirmation workflow
  const [updateConfirmation, setUpdateConfirmation] = useState<UpdateConfirmation>({
    isOpen: false,
    updateType: 'nutrition',
    data: {},
    changes: []
  });

  // State for audit trail
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [_auditTrail, setAuditTrail] = useState<ProductInfoAudit[]>([]);
  const [_loadingAudit, setLoadingAudit] = useState(false);

  // State for bulk operations
  const [showBulkUpdate, setShowBulkUpdate] = useState(false);
  const [bulkUpdateBatches, setBulkUpdateBatches] = useState<string[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [similarBatches, setSimilarBatches] = useState<BatchInfo[]>([]);

  // State for updating
  const [updating, setUpdating] = useState(false);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (batchId) {
      loadProductInformation();
    }
  }, [batchId]);

  const loadProductInformation = async () => {
    if (!batchId) return;

    try {
      setLoading(true);
      setError(null);

      // Get batch information
      const batchData = await productService.getBatch(batchId);
      setBatch({
        id: batchData.id,
        productName: batchData.productName,
        batchTitle: batchData.batchTitle,
        serialStart: batchData.serialStart,
        serialEnd: batchData.serialEnd,
        manufacturerName: 'Unknown', // TODO: Get manufacturer name from manufacturerId
        createdAt: batchData.createdAt
      });

      // Get first product to load enhanced information
      const products = await productService.getProductsByBatch(batchId, 1);
      if (products.length > 0) {
        const enhancedInfo = await enhancedProductService.getEnhancedProductInfo(products[0].id);
        if (enhancedInfo) {
          setProductInfo(enhancedInfo);
          
          // Initialize form data with existing information
          if (enhancedInfo.productType) {
            setProductType(enhancedInfo.productType as 'manufactured' | 'produce');
          }
          if ((enhancedInfo as any).produceDetails) {
            setProduceDetails((enhancedInfo as any).produceDetails);
          }
          if (enhancedInfo.nutritionInfo) {
            setNutritionInfo(enhancedInfo.nutritionInfo);
          }
          if (enhancedInfo.customContent) {
            setCustomContent(enhancedInfo.customContent);
          }
          if (enhancedInfo.manufacturingDetails) {
            setManufacturingDetails(enhancedInfo.manufacturingDetails);
          }
          if (enhancedInfo.images) {
            // Convert existing images to UploadedImage format for display
            const existingImages: ExtendedUploadedImage[] = enhancedInfo.images.map((img, index) => ({
              id: img.id,
              file: new File([], img.altText || `Image ${index + 1}`), // Placeholder file
              preview: img.url,
              name: img.altText || `Image ${index + 1}`,
              size: 0, // Size not available from display data
              status: 'success' as const,
              existingImage: true
            }));
            setProductImages(existingImages);
          }
        }
      }

    } catch (err: any) {
      console.error('Failed to load product information:', err);
      setError(err.response?.data?.message || 'Failed to load product information');
    } finally {
      setLoading(false);
    }
  };

  const loadAuditTrail = async () => {
    if (!batchId) return;

    try {
      setLoadingAudit(true);
      const trail = await enhancedProductService.getProductInfoAuditTrail(batchId);
      setAuditTrail(trail);
    } catch (err: any) {
      console.error('Failed to load audit trail:', err);
      setError('Failed to load audit trail');
    } finally {
      setLoadingAudit(false);
    }
  };

  const searchSimilarBatches = async (query: string) => {
    if (!query.trim()) {
      setSimilarBatches([]);
      return;
    }

    try {
      // This would be implemented in the product service
      // For now, we'll simulate finding similar batches
      const batches = await productService.searchBatches(query);
      const batchInfos: BatchInfo[] = batches.filter(b => b.id !== batchId).map(batch => ({
        id: batch.id,
        productName: batch.productName,
        batchTitle: batch.batchTitle,
        serialStart: batch.serialStart,
        serialEnd: batch.serialEnd,
        manufacturerName: 'Unknown', // TODO: Get manufacturer name from manufacturerId
        createdAt: batch.createdAt
      }));
      setSimilarBatches(batchInfos);
    } catch (err) {
      console.error('Failed to search batches:', err);
    }
  };

  const handleSectionEdit = (section: ProductInfoChangeType) => {
    setEditingSection(section);
    setValidationErrors({});
    setError(null);
    setSectionErrors({});
  };

  const handleCancelEdit = () => {
    setEditingSection(null);
    setValidationErrors({});
    setSectionErrors({});
    
    // Reset form data to original values
    if (productInfo) {
      setProductType((productInfo as any).productType || 'manufactured');
      setProduceDetails(((productInfo as any).produceDetails as any) || {});
      if (productInfo.nutritionInfo) {
        setNutritionInfo(productInfo.nutritionInfo);
      }
      if (productInfo.customContent) {
        setCustomContent(productInfo.customContent);
      }
      if (productInfo.manufacturingDetails) {
        setManufacturingDetails(productInfo.manufacturingDetails);
      }
    }
  };

  const validateUpdate = (updateType: ProductInfoChangeType, data: any): boolean => {
    const errors: { [key: string]: string } = {};

    switch (updateType) {
      case 'nutrition':
        {
          const nutrition = data?.nutritionInfo ?? {};
          if (nutrition.servingSize && nutrition.servingSize.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_SERVING_SIZE_LENGTH) {
            errors.servingSize = `Serving size must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_SERVING_SIZE_LENGTH} characters`;
          }
          if (
            nutrition.caloriesPerServing &&
            (nutrition.caloriesPerServing < 0 || nutrition.caloriesPerServing > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CALORIE_VALUE)
          ) {
            errors.caloriesPerServing = `Calories must be between 0 and ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CALORIE_VALUE}`;
          }
        }
        break;

      case 'custom_content':
        {
          const content = data?.customContent ?? {};
          if (content.customMessage && content.customMessage.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH) {
            errors.customMessage = `Custom message must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH} characters`;
          }
          if (content.ingredients && content.ingredients.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH) {
            errors.ingredients = `Ingredients list must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH} characters`;
          }
          if (content.usageInstructions && content.usageInstructions.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH) {
            errors.usageInstructions = `Usage instructions must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH} characters`;
          }
          if (content.brandStory && content.brandStory.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH) {
            errors.brandStory = `Brand story must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH} characters`;
          }
        }
        break;

      case 'manufacturing': {
        const details = data?.manufacturingDetails ?? data ?? {};
        if (details.mrp !== undefined && details.mrp !== null) {
          const parsed = typeof details.mrp === 'number' ? details.mrp : Number(details.mrp);
          if (!Number.isFinite(parsed) || parsed < 0 || parsed > 100000000) {
            errors.mrp = 'MRP must be between 0 and 100000000';
          }
        }
        break;
      }

      case 'produce':
        // No extra validation yet
        break;

      case 'images':
        if (productImages.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_FILES) {
          errors.images = `Maximum ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_FILES} images allowed`;
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const generateChangesSummary = (updateType: ProductInfoChangeType, data: any): string[] => {
    const changes: string[] = [];
    const hasChanges = (current: any, next: any) =>
      JSON.stringify(current ?? {}) !== JSON.stringify(next ?? {});

    switch (updateType) {
      case 'nutrition': {
        const nextNutrition = data?.nutritionInfo ?? {};
        if (hasChanges(productInfo?.nutritionInfo, nextNutrition)) {
          changes.push('Nutrition information updated');
        }
        break;
      }

      case 'custom_content': {
        const nextContent = data?.customContent ?? {};
        if (hasChanges(productInfo?.customContent, nextContent)) {
          changes.push('Custom content updated');
        }
        break;
      }

      case 'manufacturing': {
        const nextDetails = data?.manufacturingDetails ?? {};
        if (hasChanges(productInfo?.manufacturingDetails, nextDetails)) {
          changes.push('Manufacturing details updated');
        }
        break;
      }

      case 'produce': {
        const nextDetails = data?.produceDetails ?? {};
        const typeChanged = (data?.productType ?? productType) !== (productInfo as any)?.productType;
        if (typeChanged || hasChanges((productInfo as any)?.produceDetails, nextDetails)) {
          changes.push('Produce details updated');
        }
        break;
      }

      case 'images': {
        const newImageCount = productImages.filter(img => !img.existingImage).length;
        const deletedImageCount = imagesToDelete.length;
        if (newImageCount > 0) {
          changes.push(`${newImageCount} new image(s) added`);
        }
        if (deletedImageCount > 0) {
          changes.push(`${deletedImageCount} image(s) removed`);
        }
        break;
      }
    }

    return changes;
  };
  const handleSaveChanges = (updateType: ProductInfoChangeType) => {
    let data: any = {};

    switch (updateType) {
      case 'nutrition':
        data = { nutritionInfo: nutritionInfo };
        break;
      case 'custom_content':
        data = { customContent: customContent };
        break;
      case 'manufacturing':
        data = { manufacturingDetails: manufacturingDetails };
        break;
      case 'produce':
        data = { productType, produceDetails };
        break;
      case 'images':
        data = { 
          newImages: productImages.filter(img => !img.existingImage),
          deletedImages: imagesToDelete
        };
        break;
    }

    setSectionErrors(prev => ({ ...prev, [updateType]: undefined }));

    if (!validateUpdate(updateType, data)) {
      return;
    }

    const changes = generateChangesSummary(updateType, data);
    
    if (changes.length === 0) {
      setSectionErrors(prev => ({ ...prev, [updateType]: 'No changes detected' }));
      return;
    }

    // Show confirmation dialog
    setUpdateConfirmation({
      isOpen: true,
      updateType,
      data,
      changes
    });
  };

  const handleConfirmUpdate = async () => {
    if (!batchId) return;

    try {
      setUpdating(true);
      setError(null);
      setSectionErrors({});

      const { updateType, data } = updateConfirmation;

      // Handle image updates separately
      if (updateType === 'images') {
        // Delete images first
        if (imagesToDelete.length > 0) {
          await enhancedProductService.deleteImagesFromBatch(batchId, imagesToDelete);
        }

        // Add new images
        const newImages = productImages.filter(img => !img.existingImage && img.file);
        if (newImages.length > 0) {
          await enhancedProductService.addImagesToBatch(batchId, newImages.map(img => img.file!));
        }
      } else {
        // Update other information
        await enhancedProductService.updateProductInformation(
          batchId, 
          updateType as 'nutrition' | 'custom_content' | 'manufacturing' | 'produce', 
          data
        );
      }

      setSuccess(`${updateType.replace('_', ' ')} updated successfully!`);
      setUpdateConfirmation({ isOpen: false, updateType: 'nutrition', data: {}, changes: [] });
      setEditingSection(null);
      setImagesToDelete([]);

      // Reload product information
      await loadProductInformation();

    } catch (err: any) {
      console.error('Failed to update product information:', err);
      setSectionErrors(prev => ({
        ...prev,
        [updateConfirmation.updateType]: err.response?.data?.message || 'Failed to update product information'
      }));
    } finally {
      setUpdating(false);
    }
  };

  const handleImageUpload = async (files: File[]) => {
    const newImages: UploadedImage[] = files.map((file, index) => ({
      id: `new-${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      status: 'success'
    }));

    setProductImages(prev => [...prev, ...newImages]);
  };

  const handleImageRemove = (imageId: string) => {
    const imageToRemove = productImages.find(img => img.id === imageId);
    
    if (imageToRemove) {
      if (imageToRemove.existingImage) {
        // Mark existing image for deletion
        setImagesToDelete(prev => [...prev, imageId]);
      } else {
        // Clean up preview URL for new images
        URL.revokeObjectURL(imageToRemove.preview);
      }
      
      setProductImages(prev => prev.filter(img => img.id !== imageId));
    }
  };

  const renderSectionError = (section: ProductInfoChangeType) => {
    const message = sectionErrors[section];
    if (!message || editingSection !== section) return null;
    return <p className="mt-2 text-sm text-red-600">{message}</p>;
  };

  const handleBulkUpdate = async () => {
    if (!batchId || bulkUpdateBatches.length === 0) return;

    try {
      setUpdating(true);
      setError(null);

      // This would be implemented in the enhanced product service
      // For now, we'll simulate bulk update
      const results = await Promise.all(
        bulkUpdateBatches.map(async (targetBatchId) => {
          try {
            // Copy current batch's enhanced information to target batch
            const updateData = {
              nutritionInfo: Object.keys(nutritionInfo).length > 0 ? nutritionInfo : undefined,
              customContent: Object.keys(customContent).length > 0 ? customContent : undefined,
              manufacturingDetails: Object.keys(manufacturingDetails).length > 0 ? manufacturingDetails : undefined
            };

            // Update each type that has data
            const promises = [];
            if (updateData.nutritionInfo) {
              promises.push(enhancedProductService.updateProductInformation(targetBatchId, 'nutrition', { nutritionInfo: updateData.nutritionInfo }));
            }
            if (updateData.customContent) {
              promises.push(enhancedProductService.updateProductInformation(targetBatchId, 'custom_content', { customContent: updateData.customContent }));
            }
            if (updateData.manufacturingDetails) {
              promises.push(enhancedProductService.updateProductInformation(targetBatchId, 'manufacturing', { manufacturingDetails: updateData.manufacturingDetails }));
            }

            await Promise.all(promises);
            return { batchId: targetBatchId, success: true };
          } catch (error) {
            return { batchId: targetBatchId, success: false, error };
          }
        })
      );

      const successful = results.filter(r => r.success).length;
      const failed = results.filter(r => !r.success).length;

      setSuccess(`Bulk update completed! ${successful} batches updated successfully${failed > 0 ? `, ${failed} failed` : ''}.`);
      setShowBulkUpdate(false);
      setBulkUpdateBatches([]);

    } catch (err: any) {
      console.error('Bulk update failed:', err);
      setError('Bulk update failed');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product information...</p>
        </div>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-gray-600">Batch not found</p>
          <Button onClick={() => navigate('/products/create-batch')} className="mt-4">
            Back to Batch Management
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 text-gray-900">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex items-start gap-4">
          <button
            onClick={() => navigate(`/products/batch/${batchId}`)}
            className="mt-1 text-gray-600 hover:text-gray-900"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Product Information Management</h1>
            <p className="text-sm text-gray-600 mt-1">
              {formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)}
            </p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Button
            variant="outline"
            onClick={() => {
              setShowAuditTrail(true);
              loadAuditTrail();
            }}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            View Audit Trail
          </Button>

          <Button
            variant="outline"
            onClick={() => setShowBulkUpdate(true)}
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
            Bulk Update
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <section>
        {/* Alerts */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert variant="success" className="mb-6">
            {success}
          </Alert>
        )}

        {/* Batch Info */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <p className="text-sm text-blue-600 font-medium">Batch ID</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{batch.id.substring(0, 8)}...</p>
            </div>
            <div className="bg-green-50 rounded-lg p-4">
              <p className="text-sm text-green-600 font-medium">Total Products</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {(batch.serialEnd - batch.serialStart + 1).toLocaleString()}
              </p>
            </div>
            <div className="bg-purple-50 rounded-lg p-4">
              <p className="text-sm text-purple-600 font-medium">Manufacturer</p>
              <p className="text-lg font-bold text-gray-900 mt-1">{batch.manufacturerName}</p>
            </div>
            <div className="bg-orange-50 rounded-lg p-4">
              <p className="text-sm text-orange-600 font-medium">Created</p>
              <p className="text-lg font-bold text-gray-900 mt-1">
                {new Date(batch.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>

        {/* Produce Details Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Produce Details</h3>
              <p className="text-sm text-gray-600">Set product type and harvest/origin details for fresh produce.</p>
            </div>
            {editingSection !== 'produce' ? (
              <Button variant="outline" size="sm" onClick={() => handleSectionEdit('produce')}>
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Produce
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={handleCancelEdit} disabled={updating}>
                  Cancel
                </Button>
                <Button size="sm" onClick={() => handleSaveChanges('produce')} disabled={updating}>
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {renderSectionError('produce')}

          {editingSection === 'produce' ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Product Type</label>
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant={productType === 'manufactured' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setProductType('manufactured')}
                    disabled={updating}
                  >
                    Manufactured
                  </Button>
                  <Button
                    type="button"
                    variant={productType === 'produce' ? 'primary' : 'outline'}
                    size="sm"
                    onClick={() => setProductType('produce')}
                    disabled={updating}
                  >
                    Fresh produce
                  </Button>
                </div>
              </div>
              {productType === 'produce' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Harvest Date</label>
                    <Input
                      type="date"
                      value={produceDetails.harvestDate || ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, harvestDate: e.target.value }))}
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Origin / Farm / Region</label>
                    <Input
                      value={produceDetails.origin || ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, origin: e.target.value }))}
                      placeholder="e.g., Nashik, India"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Variety</label>
                    <Input
                      value={produceDetails.variety || ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, variety: e.target.value }))}
                      placeholder="e.g., Alphonso mango"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Storage / Handling</label>
                    <Input
                      value={produceDetails.storageInstructions || ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, storageInstructions: e.target.value }))}
                      placeholder="e.g., Keep refrigerated, do not freeze"
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Expiry / Best before date</label>
                    <Input
                      type="date"
                      value={produceDetails.expiryDate || ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, expiryDate: e.target.value }))}
                      disabled={updating}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Shelf life (days)</label>
                    <Input
                      type="number"
                      min={1}
                      max={365}
                      value={produceDetails.bestBeforeDays ?? ''}
                      onChange={(e) => setProduceDetails(prev => ({ ...prev, bestBeforeDays: e.target.value ? Number(e.target.value) : undefined }))}
                      placeholder="e.g., 60"
                      disabled={updating}
                    />
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div className="mb-4">
                <p className="text-sm text-gray-700">
                  Product type: <span className="font-medium">{productType === 'produce' ? 'Fresh produce' : 'Manufactured'}</span>
                </p>
              </div>
              {productType === 'produce' && (produceDetails.harvestDate || produceDetails.origin || produceDetails.variety || produceDetails.storageInstructions) ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {produceDetails.harvestDate && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Harvest Date</h4>
                      <p className="text-sm text-gray-700">{produceDetails.harvestDate}</p>
                    </div>
                  )}
                  {produceDetails.origin && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Origin / Farm / Region</h4>
                      <p className="text-sm text-gray-700">{produceDetails.origin}</p>
                    </div>
                  )}
                  {produceDetails.variety && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Variety</h4>
                      <p className="text-sm text-gray-700">{produceDetails.variety}</p>
                    </div>
                  )}
                  {produceDetails.storageInstructions && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Storage / Handling</h4>
                      <p className="text-sm text-gray-700">{produceDetails.storageInstructions}</p>
                    </div>
                  )}
                  {produceDetails.expiryDate && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Expiry / Best before date</h4>
                      <p className="text-sm text-gray-700">{produceDetails.expiryDate}</p>
                    </div>
                  )}
                  {produceDetails.bestBeforeDays && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Shelf life (days)</h4>
                      <p className="text-sm text-gray-700">{produceDetails.bestBeforeDays}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm text-gray-600">No produce details provided.</p>
              )}
            </div>
          )}
        </div>

        {/* Product Images Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Product Images</h3>
            {editingSection !== 'images' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSectionEdit('images')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Images
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSaveChanges('images')}
                  disabled={updating}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>
          {renderSectionError('images')}

          {editingSection === 'images' ? (
            <div>
              <ImageUpload
                maxFiles={5}
                maxFileSize={5 * 1024 * 1024}
                acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                onUpload={handleImageUpload}
                onRemove={handleImageRemove}
                disabled={updating}
                images={productImages}
                className="mb-4"
              />
              {validationErrors.images && (
                <p className="text-sm text-red-600">{validationErrors.images}</p>
              )}
            </div>
          ) : (
            <div>
              {productInfo?.images && productInfo.images.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {productInfo.images.map((image) => (
                    <div key={image.id} className="relative">
                      <img
                        src={image.thumbnailUrl}
                        alt={image.altText}
                        className="w-full h-32 object-cover rounded-lg border border-gray-200"
                      />
                      {image.isPrimary && (
                        <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs px-2 py-1 rounded">
                          Primary
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  <p>No product images uploaded</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nutrition Information Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Nutrition Information</h3>
            {editingSection !== 'nutrition' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSectionEdit('nutrition')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Nutrition
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSaveChanges('nutrition')}
                  disabled={updating}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {renderSectionError('nutrition')}

          {editingSection === 'nutrition' ? (
            <NutritionInformationForm
              nutritionInfo={nutritionInfo}
              onChange={(field, value) => setNutritionInfo(prev => ({ ...prev, [field]: value }))}
              disabled={updating}
              errors={validationErrors}
            />
          ) : (
            <div>
              {productInfo?.nutritionInfo ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Basic Information</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Serving Size:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.servingSize}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Calories per Serving:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.caloriesPerServing}</span>
                      </div>
                      {productInfo.nutritionInfo.servingsPerContainer && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Servings per Container:</span>
                          <span className="font-medium">{productInfo.nutritionInfo.servingsPerContainer}</span>
                        </div>
            )}
          </div>
        </div>
                  
                  <div>
                    <h4 className="font-medium text-gray-900 mb-2">Macronutrients</h4>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Protein:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.macronutrients.protein}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Carbohydrates:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.macronutrients.carbohydrates}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Total Fat:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.macronutrients.totalFat}g</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Sodium:</span>
                        <span className="font-medium">{productInfo.nutritionInfo.macronutrients.sodium}mg</span>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p>No nutrition information provided</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Custom Content Section */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Custom Content</h3>
            {editingSection !== 'custom_content' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSectionEdit('custom_content')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Content
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSaveChanges('custom_content')}
                  disabled={updating}
                >
                  Save Changes
                </Button>
              </div>
            )}
          </div>

          {renderSectionError('custom_content')}

          {editingSection === 'custom_content' ? (
            <CustomContentForm
              customContent={customContent}
              onChange={(field, value) => setCustomContent(prev => ({ ...prev, [field]: value }))}
              disabled={updating}
              errors={validationErrors}
            />
          ) : (
            <div>
              {productInfo?.customContent ? (
                <div className="space-y-4">
                  {productInfo.customContent.ingredients && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Ingredients</h4>
                      <p className="text-sm text-gray-700">{productInfo.customContent.ingredients}</p>
                    </div>
                  )}
                  
                  {productInfo.customContent.allergens && productInfo.customContent.allergens.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Allergens</h4>
                      <div className="flex flex-wrap gap-2">
                        {productInfo.customContent.allergens.map((allergen, index) => (
                          <span key={index} className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                            {allergen}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {productInfo.customContent.usageInstructions && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Usage Instructions</h4>
                      <p className="text-sm text-gray-700">{productInfo.customContent.usageInstructions}</p>
                    </div>
                  )}
                  
                  {productInfo.customContent.customMessage && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-2">Custom Message</h4>
                      <div className="text-sm text-gray-700" dangerouslySetInnerHTML={{ __html: productInfo.customContent.customMessage }} />
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                  </svg>
                  <p>No custom content provided</p>
                </div>
              )}
            </div>
          )}
        </div>

        {productType !== 'produce' && (
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Manufacturing Details</h3>
            {editingSection !== 'manufacturing' ? (
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleSectionEdit('manufacturing')}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Details
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleCancelEdit}
                  disabled={updating}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={() => handleSaveChanges('manufacturing')}
                  disabled={updating}
                >
                  Save Changes
                </Button>
              </div>
            )}
            </div>

            {renderSectionError('manufacturing')}

            {editingSection === 'manufacturing' ? (
            <ManufacturingDetailsForm
              manufacturingDetails={manufacturingDetails}
              onChange={(field, value) => setManufacturingDetails(prev => ({ ...prev, [field]: value }))}
              disabled={updating}
              errors={validationErrors}
            />
            ) : (
              <div>
                {productInfo?.manufacturingDetails ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {productInfo.manufacturingDetails.manufacturingDate && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Manufacturing Date</h4>
                      <p className="text-sm text-gray-700">
                        {new Date(productInfo.manufacturingDetails.manufacturingDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  
                  {productInfo.manufacturingDetails.expiryDate && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Expiry Date</h4>
                      <p className="text-sm text-gray-700">
                        {new Date(productInfo.manufacturingDetails.expiryDate).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  
                  {productInfo.manufacturingDetails.batchNumber && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Batch Number</h4>
                      <p className="text-sm text-gray-700">{productInfo.manufacturingDetails.batchNumber}</p>
                    </div>
                  )}

                  {productInfo.manufacturingDetails.mrp !== undefined && productInfo.manufacturingDetails.mrp !== null && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">MRP (INR)</h4>
                      <p className="text-sm text-gray-700">
                        {Number(productInfo.manufacturingDetails.mrp).toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                      </p>
                    </div>
                  )}
                  
                  {productInfo.manufacturingDetails.facilityLocation && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Facility Location</h4>
                      <p className="text-sm text-gray-700">{productInfo.manufacturingDetails.facilityLocation}</p>
                    </div>
                  )}
                  </div>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 7.172V5L8 4z" />
                    </svg>
                    <p>No manufacturing details provided</p>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </section>

      {/* Update Confirmation Modal */}
      {updateConfirmation.isOpen && (
        <Modal
          isOpen={updateConfirmation.isOpen}
          onClose={() => setUpdateConfirmation({ isOpen: false, updateType: 'nutrition', data: {}, changes: [] })}
          title="Confirm Information Update"
        >
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-800">
                You are about to update the <strong>{updateConfirmation.updateType.replace('_', ' ')}</strong> information for this batch.
                This will affect all {(batch.serialEnd - batch.serialStart + 1).toLocaleString()} products in the batch.
              </p>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-900 mb-2">Changes to be applied:</h4>
              <ul className="text-sm text-gray-700 space-y-1">
                {updateConfirmation.changes.map((change, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="text-green-600 mt-0.5">•</span>
                    <span>{change}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => setUpdateConfirmation({ isOpen: false, updateType: 'nutrition', data: {}, changes: [] })}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleConfirmUpdate}
                disabled={updating}
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  'Confirm Update'
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Audit Trail Modal */}
      {showAuditTrail && (
        <Modal
          isOpen={showAuditTrail}
          onClose={() => setShowAuditTrail(false)}
          title="Product Information Audit Trail"
          size="large"
        >
          <ProductInfoAuditTrail
            batchId={batchId!}
            onClose={() => setShowAuditTrail(false)}
          />
        </Modal>
      )}

      {/* Bulk Update Modal */}
      {showBulkUpdate && (
        <Modal
          isOpen={showBulkUpdate}
          onClose={() => setShowBulkUpdate(false)}
          title="Bulk Update Similar Products"
          size="large"
        >
          <div className="space-y-4">
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
              <p className="text-sm text-yellow-800">
                <strong>Warning:</strong> Bulk update will copy the current batch's enhanced information to selected batches.
                This action cannot be undone. Please review carefully before proceeding.
              </p>
            </div>

            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700 mb-2">
                Search for similar batches
              </label>
              <Input
                id="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  searchSimilarBatches(e.target.value);
                }}
                placeholder="Search by product name..."
                className="mb-4"
              />
            </div>

            {similarBatches.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-2">Select batches to update:</h4>
                <div className="max-h-64 overflow-y-auto space-y-2">
                  {similarBatches.map((similarBatch) => (
                    <div key={similarBatch.id} className="flex items-center justify-between p-3 border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          checked={bulkUpdateBatches.includes(similarBatch.id)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setBulkUpdateBatches(prev => [...prev, similarBatch.id]);
                            } else {
                              setBulkUpdateBatches(prev => prev.filter(id => id !== similarBatch.id));
                            }
                          }}
                          className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                          aria-label={`Select batch ${similarBatch.id} for bulk update`}
                        />
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {formatBatchName(similarBatch.productName, similarBatch.createdAt, similarBatch.batchTitle)}
                          </p>
                          <p className="text-xs text-gray-500">
                            {(similarBatch.serialEnd - similarBatch.serialStart + 1).toLocaleString()} products
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">
                          {new Date(similarBatch.createdAt).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkUpdate(false);
                  setBulkUpdateBatches([]);
                  setSearchQuery('');
                  setSimilarBatches([]);
                }}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                onClick={handleBulkUpdate}
                disabled={bulkUpdateBatches.length === 0 || updating}
              >
                {updating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Updating...
                  </>
                ) : (
                  `Update ${bulkUpdateBatches.length} Batch${bulkUpdateBatches.length !== 1 ? 'es' : ''}`
                )}
              </Button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
